/**
 * Shared identity-trait logic for the Segment event-enrichment layer, used by
 * both transports — the native plugin (`UserTraitsPlugin.ts`) and the web
 * source middleware (`analytics.web.ts`). Keeping the merge/derive/inject logic
 * here (rather than duplicated per transport) means one set of unit tests
 * covers both paths and the two can't drift.
 *
 * Pure functions only — no Segment SDK dependency — so either transport can
 * call them.
 */

/** Identity traits cached from the most recent `identify()` for the user. */
export type IdentityTraits = {
  email?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  userType?: string;
};

/**
 * Merge incoming identify traits over the cached ones. A partial identify
 * (e.g. the login path, which omits `userType`) must not wipe a trait an
 * earlier identify already set, so each key falls back to the cached value.
 * When the identify is for a different user (`sameUser` false) the cache is
 * ignored so a new user can't inherit the previous user's traits.
 */
export function mergeIdentityTraits(
  cached: IdentityTraits,
  incoming: IdentityTraits,
  sameUser: boolean
): IdentityTraits {
  const base = sameUser ? cached : {};

  return {
    email: incoming.email ?? base.email,
    firstName: incoming.firstName ?? base.firstName,
    lastName: incoming.lastName ?? base.lastName,
    name: incoming.name ?? base.name,
    userType: incoming.userType ?? base.userType,
  };
}

/**
 * Resolve the full name for the `user_name` event property: `firstName` +
 * `lastName` concatenated, falling back to the legacy `name` trait (deprecated
 * but still often populated) when first/last are empty. Returns `undefined`
 * when nothing is available, so callers don't emit an empty `user_name`.
 */
export function resolveUserName(traits: IdentityTraits): string | undefined {
  const fromParts = [traits.firstName, traits.lastName].filter(Boolean).join(' ');

  return fromParts || traits.name || undefined;
}

/**
 * Inject the canonical snake_case identity properties onto an event's
 * `properties` object, in place. Matches the keys primum-app and the API both
 * send (`user_id`, `user_email`, `user_name`, `user_type`). Each key is added
 * only when its source value is present, so absent traits leave no empty
 * properties. The Segment envelope `userId` is left untouched (additive).
 */
export function enrichIdentityProperties(
  properties: Record<string, unknown>,
  userId: string | undefined,
  traits: IdentityTraits
): void {
  if (userId) {
    properties.user_id = userId;
  }

  if (traits.email) {
    properties.user_email = traits.email;
  }

  const name = resolveUserName(traits);

  if (name) {
    properties.user_name = name;
  }

  if (traits.userType) {
    properties.user_type = traits.userType;
  }
}
